import {
    RiMailLine,
} from "@remixicon/react";
import React, {
    DependencyList, ReactNode, useContext, useEffect,
} from "react";
import {
    ChannelType, DisplayText,
    Settings,
} from "../../../__generated__/api";
import { ReactComponent as GroupsList } from "../../../assets/icons/groups-list.svg";
import { ReactComponent as MicrosoftTeamsLogo } from "../../../assets/img/microsoft-teams.svg";
import { ReactComponent as SlackLogo } from "../../../assets/img/slack-logo.svg";

import { isHtmlEqual } from "../../../api";
import Styles from "../Administrator.module.scss";

export interface SettingsProps {
    settings?: Settings;
    isLoading?: boolean;
    isV3: boolean;
}

interface PageController {
    settings?: Settings;
    setTitle: (title: ReactNode) => void;
}

export const SettingsPageContext = React.createContext<PageController>({
    settings: undefined,
    setTitle: () => {
        // nothing
    },
});

export function useSettingsPageContext(): PageController {
    return useContext(SettingsPageContext);
}

export function useSetTitle(title: ReactNode, dependencies: DependencyList): void {
    const { setTitle } = useSettingsPageContext();
    useEffect(() => {
        if (title) {
            setTitle(title);
        }
    }, dependencies);
}

export const SOURCES = {
    [ChannelType.Slack]: {
        text: "Slack",
        icon: SlackLogo,
        filterIcon: <SlackLogo className={Styles["nav-icon"]} />,
        tableIcon: <SlackLogo className={Styles["channel-icon"]} />,
        mediumIcon: <SlackLogo className={Styles["medium-icon"]} />,
    },
    [ChannelType.MicrosoftTeams]: {
        text: "Microsoft Teams",
        icon: MicrosoftTeamsLogo,
        filterIcon: <MicrosoftTeamsLogo className={Styles["nav-icon"]} />,
        tableIcon: <MicrosoftTeamsLogo className={Styles["channel-icon"]} />,
        mediumIcon: <MicrosoftTeamsLogo className={Styles["medium-icon"]} />,
    },
    [ChannelType.ChannelTag]: {
        text: "Channel Group",
        icon: GroupsList,
        filterIcon: <GroupsList className={Styles["nav-icon"]} />,
        tableIcon: <GroupsList className={Styles["channel-icon"]} />,
        mediumIcon: <GroupsList className={Styles["medium-icon"]} />,
    },
    [ChannelType.InboundSupportEmail]: {
        text: "Email",
        icon: RiMailLine,
        filterIcon: <RiMailLine className={Styles["nav-icon"]} />,
        tableIcon: <RiMailLine className={Styles["channel-icon"]} />,
        mediumIcon: <RiMailLine className={Styles["medium-icon"]} />,
    },
};

function isDisplayText(value: unknown): value is DisplayText {
    const valueAsDisplay = value as DisplayText;
    return valueAsDisplay != null && (valueAsDisplay.__typename === "DisplayText" || valueAsDisplay.rawText != null);
}

export const DEEP_COMPARER = (__: string[], left: unknown, right: unknown) => {
    if (isDisplayText(left) || isDisplayText(right)) {
        const leftText = isDisplayText(left) ? left.rawText : left;
        const rightText = isDisplayText(right) ? right.rawText : right;
        return isHtmlEqual(leftText as string, rightText as string);
    }

    if ((Array.isArray(left) && left.isEmpty && right == null)
        || (Array.isArray(right) && right.isEmpty && left == null)
        || (!left && !right)) {
        return true;
    }

    if (typeof left === "object" && typeof right === "object") {
        const hasLeftValues = left ? Object.values(left).some(item => item != null) : false;
        const hasRightValues = right ? Object.values(right).some(item => item != null) : false;
        if (!hasLeftValues && !hasRightValues) {
            return true;
        }
    }

    return undefined;
};
